// "Has the player tried to submit the payment?"
//
// The Deposit / Withdrawal switch belongs to the time before a submission. Once
// the player has tried to pay, the transaction that belongs to the current tab is
// under way or has been answered, so no screen may offer the other tab. One
// pure function of the whole state decides it, so the tab bar, the classic
// labels and the switch itself (`switchTransactionTab`) cannot disagree.
//
// The rule is true when any of these holds:
//  - a submission is in progress (`limitSubmit`);
//  - the pay reply is stored (`resultOfPay`), which also covers the retry, the
//    open-banking and the APM decline replies, the provider redirect screens
//    and the result page;
//  - the decline recovery settings are set in the location (a reload into
//    recovery has no stored reply yet);
//  - the fatal error is set (`errors.app_fatal_error`; its default payload is
//    `{}`, which is truthy on purpose, so any fatal error counts);
//  - the amount-first page reports its own flags (`submitting`,
//    `recoveryAutoSubmit`), which rise before `limitSubmit` does.
//
// A stored reply is also written by start-up paths that involve no submission
// (for example the empty-methods states). Those screens are result-page
// screens without a tab bar, so counting them as "after" changes nothing that
// is visible.
//
// Every part of the state is read with optional chaining: hand-built minimal
// states and the boot header must not throw. `RESET_TAB_STATE` clears every
// state fact, so a switch that was allowed starts the new tab with the rule
// false.

export interface SubmissionPageFlags {
  submitting?: boolean;
  recoveryAutoSubmit?: boolean;
}

const isTrue = (value: unknown): boolean =>
  value === true || value === 1 || value === '1' || value === 'true';

export const hasTriedToSubmit = (state: any, pageFlags?: SubmissionPageFlags): boolean => {
  if (pageFlags?.submitting || pageFlags?.recoveryAutoSubmit) return true;
  if (state?.limitSubmit) return true;
  if (state?.resultOfPay) return true;

  const recoverySettings = state?.location?.recoverySettingsObject;
  if (isTrue(recoverySettings?.ob_decline_recovery) || isTrue(recoverySettings?.retry_with_apm)) {
    return true;
  }

  return !!state?.errors?.app_fatal_error;
};
